import React, { useState } from 'react';
import {
  Mail, Phone, MapPin, Send, CheckCircle,
  Sparkles, Clock, MessageSquare, User
} from 'lucide-react';
import API_URL from '../config';


const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.message) {
      alert('Please fill out all required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const json = await response.json();
      if (json.success) {
        setIsSubmitted(true);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      } else {
        alert(json.message || 'Failed to send message.');
      }
    } catch (error) {
      console.error('Error submitting message:', error);
      alert('Failed to connect to the backend server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactItems = [
    {
      icon: <Mail size={20} />,
      label: 'Email Us',
      value: 'Prakashupadhyaya835@gmail.com',
      href: 'mailto:Prakashupadhyaya835@gmail.com',
    },
    {
      icon: <Phone size={20} />,
      label: 'Call Us',
      value: '+61 416 656 862',
      href: 'tel:+61416656862',
    },
    {
      icon: <MapPin size={20} />,
      label: 'Our Location',
      value: '26 Naretha Court, Morayfield QLD 4506',
      href: 'https://maps.google.com/?q=Morayfield+QLD+4506',
    },
    {
      icon: <Clock size={20} />,
      label: 'Office Hours',
      value: 'Mon – Sat: 9:00 AM – 6:00 PM',
      href: null,
    },
  ];

  return (
    <div className="appt-page">

      {/* ── Hero — exact same structure as Appointment hero ──────────── */}
      <div className="appt-hero">
        <div className="appt-hero-badge">
          <Sparkles size={16} />
          <span>Connect With Us</span>
        </div>
        <h1 className="appt-hero-title">
          Get In <span>Touch</span>
        </h1>
        <p className="appt-hero-sub">
          Whether you have questions about yoga, astrology, or our services — we're always
          here for you. Reach out and we'll respond within 24 hours.
        </p>
      </div>

      {/* ── Main Content ─────────────────────────────────────────────── */}
      <div className="appt-container">

        {/* ── Contact Info Cards ──────────────────────────────────────── */}
        <div className="contact-cards-row appt-fade-in">
          {contactItems.map((item, idx) => (
            <div key={idx} className="appt-card contact-info-tile">
              <div className="appt-card-header" style={{ marginBottom: 12, paddingBottom: 12 }}>
                {item.icon}
                <h3>{item.label}</h3>
              </div>
              {item.href ? (
                <a
                  href={item.href}
                  className="contact-tile-value contact-tile-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  {item.value}
                </a>
              ) : (
                <p className="contact-tile-value">{item.value}</p>
              )}
            </div>
          ))}
        </div>

        {/* ── Form + Side Panel ────────────────────────────────────────── */}
        <div className="contact-split appt-fade-in">

          {/* Left info panel */}
          <div className="appt-card contact-left-panel">
            <div className="appt-card-header">
              <MessageSquare size={20} />
              <h3>Send a Message</h3>
            </div>

            <p className="contact-panel-desc">
              Fill in your details and we'll get back to you within 24 hours.
              Every enquiry is handled personally with care and dedication.
            </p>

            <div className="contact-panel-divider" />

            <div className="contact-promise-list">
              {[
                'Personalised responses',
                'Reply within 24 hours',
                'Your data stays private',
              ].map((item, i) => (
                <div key={i} className="contact-promise-row">
                  <CheckCircle size={15} className="contact-promise-icon" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Contact quick-refs */}
            <div className="contact-panel-refs">
              <a href="mailto:Prakashupadhyaya835@gmail.com" className="contact-panel-ref">
                <Mail size={14} />
                Prakashupadhyaya835@gmail.com
              </a>
              <a href="tel:+61416656862" className="contact-panel-ref">
                <Phone size={14} />
                +61 416 656 862
              </a>
              <div className="contact-panel-ref">
                <MapPin size={14} />
                26 Naretha Court, Morayfield QLD 4506
              </div>
            </div>

            {/* Om watermark */}
            <div className="contact-om-mark" aria-hidden="true">ॐ</div>
          </div>

          {/* Right form */}
          <div className="appt-card contact-right-form">
            {isSubmitted ? (
              <div className="appt-success-wrap" style={{ margin: 0, boxShadow: 'none', padding: '40px 20px' }}>
                <div className="appt-success-icon">
                  <CheckCircle size={52} />
                </div>
                <h2>Message Sent!</h2>
                <p className="appt-success-sub">
                  Thank you for reaching out. Prakash Upadhyaya will get back to you shortly.
                </p>
                <button
                  className="appt-btn-primary"
                  onClick={() => setIsSubmitted(false)}
                  id="contact-send-another"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="contact-form">
                <div className="appt-card-header">
                  <User size={20} />
                  <h3>Your Information</h3>
                </div>

                {/* Booking-summary-style note bar */}
                <div className="appt-booking-summary" style={{ marginBottom: 24 }}>
                  <div><Sparkles size={14} /> All fields marked with <strong style={{ color: '#ef4444' }}>*</strong> are required</div>
                </div>

                <div className="contact-form-grid">
                  <div className="appt-field">
                    <label htmlFor="contact-fname">
                      <User size={14} /> First Name *
                    </label>
                    <input
                      id="contact-fname"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Your first name"
                    />
                  </div>

                  <div className="appt-field">
                    <label htmlFor="contact-lname">
                      <User size={14} /> Last Name *
                    </label>
                    <input
                      id="contact-lname"
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Your last name"
                    />
                  </div>

                  <div className="appt-field">
                    <label htmlFor="contact-email">
                      <Mail size={14} /> Email Address *
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="appt-field">
                    <label htmlFor="contact-phone">
                      <Phone size={14} /> Phone Number *
                    </label>
                    <input
                      id="contact-phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+61 4XX XXX XXX"
                    />
                  </div>

                  <div className="appt-field appt-field-full">
                    <label htmlFor="contact-message">
                      <MessageSquare size={14} /> Comments / Questions *
                    </label>
                    <textarea
                      id="contact-message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Describe what you'd like to discuss..."
                    />
                  </div>
                </div>

                <div className="appt-footer">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="appt-btn-primary"
                    id="contact-submit"
                  >
                    {isSubmitting ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="contact-spinner" /> Sending…
                      </span>
                    ) : (
                      <><Send size={17} /> Send Message</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* ── Google Map ───────────────────────────────────────────────── */}
        <div className="contact-map-block appt-fade-in">
          <div className="appt-card-header contact-map-header">
            <MapPin size={20} />
            <h3>Our Location — 26 Naretha Court, Morayfield QLD 4506</h3>
          </div>
          <div className="contact-map-frame">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3524.123456789!2d152.95!3d-26.80!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b93e6c0a1234567%3A0x1234567890abcdef!2sMoreyfield%2C%20QLD%204506!5e0!3m2!1sen!2sau!4v1689000000000!5m2!1sen!2sau"
              width="100%"
              height="340"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Location Map - Morayfield, QLD, Australia"
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Contact;
